import { type FC, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import styled from "styled-components";
import { theme } from "../../theme";
import Subheading from "../atoms/typography/Subheading";
import { CrossIcon } from "../atoms/icons/CrossIcon";

const Backdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.1);
  z-index: 999;
`;

const Panel = styled(motion.div)<{
  $direction: "left" | "right";
  $width: string;
}>`
  position: fixed;
  top: 0;
  bottom: 0;
  ${({ $direction }) => ($direction === "left" ? "left: 0;" : "right: 0;")}
  width: ${({ $width }) => $width};
  max-width: 90vw;
  background: white;
  z-index: 1000;
  box-shadow: ${({ $direction }) =>
    $direction === "left"
      ? "4px 0 24px rgba(0, 0, 0, 0.15)"
      : "-4px 0 24px rgba(0, 0, 0, 0.15)"};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${theme.colors.grey05};
  flex-shrink: 0;
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;

const CloseButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  direction?: "left" | "right";
  width?: string;
  headerActions?: ReactNode;
  children: ReactNode;
}

const SidePanel: FC<Props> = ({
  isOpen,
  onClose,
  title,
  direction = "left",
  width = "600px",
  headerActions,
  children,
}) => {
  const exitX = direction === "left" ? "-100%" : "100%";

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <Backdrop
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <Panel
            $direction={direction}
            $width={width}
            initial={{ x: exitX }}
            animate={{ x: 0 }}
            exit={{ x: exitX }}
            transition={{ type: "spring", damping: 28, stiffness: 300 }}
          >
            <Header>
              <Subheading color={theme.colors.primary}>{title}</Subheading>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                {headerActions}
                <CloseButton onClick={onClose}>
                  <CrossIcon color={theme.colors.primary} />
                </CloseButton>
              </div>
            </Header>
            <Content>{children}</Content>
          </Panel>
        </>
      )}
    </AnimatePresence>
  );
};

export default SidePanel;
